/**
 * Geolocation API 훅
 * 사용자 위치 획득을 위한 공통 훅
 */
import { useState, useEffect, useCallback } from 'react';

export interface GeolocationState {
  lat: number | null;
  lng: number | null;
  loading: boolean;
  error: GeolocationPositionError | null;
  /** 위치 정보 획득 여부 */
  hasLocation: boolean;
  /** 위치 권한 상태 */
  permissionState: PermissionState | null;
}

interface UseGeolocationOptions {
  /** 자동으로 위치 요청 (기본: false) */
  autoRequest?: boolean;
  /** 위치 정확도 (기본: false = 저정확도, 빠름) */
  enableHighAccuracy?: boolean;
  /** 타임아웃 (ms, 기본: 10000) */
  timeout?: number;
  /** 캐시된 위치 최대 수명 (ms, 기본: 300000 = 5분) */
  maximumAge?: number;
}

const DEFAULT_OPTIONS: UseGeolocationOptions = {
  autoRequest: false,
  enableHighAccuracy: false,
  timeout: 10000,
  maximumAge: 300000,
};

/**
 * 사용자 위치 획득 훅
 * @param options Geolocation 옵션
 */
export function useGeolocation(options: UseGeolocationOptions = {}): GeolocationState & {
  requestLocation: () => void;
  clearLocation: () => void;
} {
  const mergedOptions = { ...DEFAULT_OPTIONS, ...options };

  // autoRequest가 true면 처음부터 loading 상태로 시작
  // 이렇게 해야 useAllStores가 위치 결정 완료 전까지 대기함
  const [state, setState] = useState<GeolocationState>(() => ({
    lat: null,
    lng: null,
    loading: mergedOptions.autoRequest ?? false,
    error: null,
    hasLocation: false,
    permissionState: null,
  }));

  // 권한 상태 확인
  useEffect(() => {
    if (!navigator.permissions) return;

    navigator.permissions.query({ name: 'geolocation' }).then((result) => {
      setState((prev) => ({ ...prev, permissionState: result.state }));

      result.addEventListener('change', () => {
        setState((prev) => ({ ...prev, permissionState: result.state }));
      });
    }).catch(() => {
      // permissions API 미지원
    });
  }, []);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setState((prev) => ({
        ...prev,
        error: {
          code: 2,
          message: 'Geolocation is not supported',
          PERMISSION_DENIED: 1,
          POSITION_UNAVAILABLE: 2,
          TIMEOUT: 3,
        } as GeolocationPositionError,
      }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setState({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          loading: false,
          error: null,
          hasLocation: true,
          permissionState: 'granted',
        });
      },
      (error) => {
        setState((prev) => ({
          ...prev,
          loading: false,
          error,
          hasLocation: false,
          permissionState: error.code === 1 ? 'denied' : prev.permissionState,
        }));
      },
      {
        enableHighAccuracy: mergedOptions.enableHighAccuracy,
        timeout: mergedOptions.timeout,
        maximumAge: mergedOptions.maximumAge,
      }
    );
  }, [mergedOptions.enableHighAccuracy, mergedOptions.timeout, mergedOptions.maximumAge]);

  const clearLocation = useCallback(() => {
    setState((prev) => ({
      ...prev,
      lat: null,
      lng: null,
      hasLocation: false,
    }));
  }, []);

  // 자동 요청
  useEffect(() => {
    if (mergedOptions.autoRequest) {
      requestLocation();
    }
  }, [mergedOptions.autoRequest, requestLocation]);

  return {
    ...state,
    requestLocation,
    clearLocation,
  };
}

/**
 * URL 쿼리 파라미터에서 위치 정보 추출
 */
export function getLocationFromSearchParams(
  searchParams: URLSearchParams
): { lat: number; lng: number } | null {
  const latStr = searchParams.get('lat');
  const lngStr = searchParams.get('lng');

  if (!latStr || !lngStr) return null;

  const lat = parseFloat(latStr);
  const lng = parseFloat(lngStr);

  if (isNaN(lat) || isNaN(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;

  return { lat, lng };
}
